import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { dvRedeem, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Motion";
import { setMeta } from "../lib/seo";
import { track as trackEvent } from "../lib/analytics";

export default function DimensionVAccess() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMeta({
      title: "Dimension V Access — Motion & Meaning",
      description: "Redeem your Dimension V access credential.",
      path: "/dimension-v/access",
      noindex: true,
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await dvRedeem(code.trim());
      setSuccess(true);
      trackEvent("dv_redeemed");
      refresh && refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "This credential is not valid.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#030303] overflow-hidden" data-testid="dv-access-page">
      <div className="signal-static" />
      <div className="mm-vignette" />
      <div className="relative w-full max-w-xl px-6 py-40 text-center">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.55em] text-red-500/80">DIMENSION V</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-white mt-5">ACCESS VERIFICATION</h1>
        </Reveal>

        {/* STATE: success */}
        {success ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mt-14" data-testid="dv-access-success">
            <p className="font-mono text-xs tracking-[0.5em] text-gold">ACCESS VERIFIED</p>
            <p className="font-serif text-3xl md:text-4xl font-light text-white mt-6">DIMENSION V HAS OPENED.</p>
            <p className="text-white/45 mt-5 text-sm leading-relaxed">
              Your access is now permanently linked to this account. You will never need this key again.
            </p>
            <button onClick={() => navigate("/dimension-v")} data-testid="dv-access-enter"
              className="mt-10 bg-gold text-black font-mono text-[11px] tracking-[0.3em] px-12 py-4 hover:bg-white transition-colors">
              ENTER DIMENSION V
            </button>
          </motion.div>

        /* STATE: already entitled */
        ) : user && user.dimensionV === "active" ? (
          <div className="mt-14" data-testid="dv-access-already">
            <p className="text-white/60">Your Dimension V access is already active.</p>
            <button onClick={() => navigate("/dimension-v")} data-testid="dv-access-enter-existing"
              className="mt-8 bg-gold text-black font-mono text-[11px] tracking-[0.3em] px-12 py-4 hover:bg-white transition-colors">
              ENTER DIMENSION V
            </button>
          </div>

        /* STATE: not logged in */
        ) : user === false ? (
          <div className="mt-14" data-testid="dv-access-signin">
            <p className="text-white/50 leading-relaxed max-w-sm mx-auto">
              Dimension V access is bound permanently to a Motion &amp; Meaning account.
              Sign in — or create your account — to redeem your credential.
            </p>
            <div className="flex flex-wrap justify-center gap-5 mt-10">
              <Link to="/login?next=/dimension-v/access" data-testid="dv-access-login"
                className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors">SIGN IN</Link>
              <Link to="/register?next=/dimension-v/access" data-testid="dv-access-register"
                className="border border-white/30 text-white font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:border-gold hover:text-gold transition-colors">CREATE ACCOUNT</Link>
            </div>
          </div>

        /* STATE: logged in, redeem form */
        ) : user ? (
          <form onSubmit={submit} className="mt-14" data-testid="dv-access-form">
            <p className="text-white/50 text-sm leading-relaxed max-w-sm mx-auto">
              Enter the access credential issued for your verified Multidimensional Complete Set.
              It can be redeemed once.
            </p>
            <input
              type="text" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              placeholder="DV5-XXXXX-XXXXX-XXXXX-XXXXX"
              autoComplete="off" spellCheck={false}
              data-testid="dv-access-input"
              aria-label="Dimension V access credential"
              className="w-full mt-8 bg-[#0a0a0a] border border-white/15 focus:border-gold outline-none px-5 py-4 text-center font-mono text-sm tracking-[0.2em] text-white placeholder:text-white/20 transition-colors"
            />
            {error && (
              <p className="font-mono text-[10px] tracking-[0.2em] text-red-400 mt-4" data-testid="dv-access-error">{error}</p>
            )}
            <button type="submit" disabled={busy || !code.trim()} data-testid="dv-access-submit"
              className="mt-8 bg-gold text-black font-mono text-[11px] tracking-[0.3em] px-12 py-4 hover:bg-white transition-colors disabled:opacity-40 disabled:hover:bg-gold">
              {busy ? "VERIFYING…" : "VERIFY ACCESS"}
            </button>
            <p className="font-mono text-[9px] tracking-[0.2em] text-white/25 mt-8">
              KEYS ARE ISSUED ONLY FOR VERIFIED COMPLETE SET ORDERS.
            </p>
          </form>
        ) : (
          <div className="min-h-[200px]" />
        )}
      </div>
    </div>
  );
}
